import { useState, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@ui/Button"
import { Dialog } from "@ui/Dialog"
import { Input } from "@ui/Input"
import LocationIcon from "../../icons/cisco-momentum/core/location-regular.svg?react"
import SearchIcon from "../../icons/cisco-momentum/core/search-regular.svg?react"

export default function SpaceLinkModal({ isOpen, onClose, courseId }) {
  const [ searchTerm, setSearchTerm ] = useState("")
  const [ results, setResults ] = useState([])
  const [ totalCount, setTotalCount ] = useState(0)
  const [ searching, setSearching ] = useState(false)
  const [ selectedId, setSelectedId ] = useState(null)
  const searchTimeout = useRef(null)

  useEffect(() => {
    if (isOpen) {
      fetchSpaces("")
    } else {
      setSearchTerm("")
      setResults([])
      setSelectedId(null)
    }
  }, [ isOpen ])

  async function fetchSpaces(q) {
    setSearching(true)
    try {
      const url = q
        ? `/lti/courses/${courseId}/course_spaces?q=${encodeURIComponent(q)}`
        : `/lti/courses/${courseId}/course_spaces`
      const res = await fetch(url)
      const data = await res.json()
      setResults(data.spaces || [])
      setTotalCount(data.total || 0)
    } catch (err) {
      console.error("Space search error:", err)
    } finally {
      setSearching(false)
    }
  }

  function handleSearch(q) {
    setSearchTerm(q)
    setSelectedId(null)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      setSearching(true)
      fetchSpaces(q)
    }, 300)
  }

  function handleAdd() {
    if (!selectedId) return
    router.post(`/lti/courses/${courseId}/course_spaces`, { scheduled_space_id: selectedId })
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Content size="sm">
        <Dialog.Header>
          <Dialog.Title>Link to Space</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className="space-y-4">
            <Input
              type="text"
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search spaces..."
              leadingIcon={<SearchIcon aria-hidden="true" />}
            />

            <div className="min-h-[256px] max-h-64 overflow-y-auto border border-webex-gray-300 dark:border-webex-gray-700 rounded-md relative">
              {results.length === 0 && !searching ? (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-webex-gray-600 dark:text-webex-gray-400">
                  {searchTerm ? "No matching spaces found" : "No available spaces"}
                </div>
              ) : (
                <ul className={`divide-y divide-webex-gray-200 dark:divide-webex-gray-800 ${searching ? "opacity-50" : ""}`}>
                  {results.map(space => (
                    <li key={space.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(space.id)}
                        className={`w-full px-4 py-3 text-left transition-colors motion-reduce:transition-none ${
                          selectedId === space.id
                            ? "bg-webex-blue-100 dark:bg-webex-blue-900"
                            : "hover:bg-webex-gray-50 dark:hover:bg-webex-gray-900"
                        }`}
                      >
                        <div className={`text-sm flex items-center gap-2 ${
                          selectedId === space.id ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-1000 dark:text-white"
                        }`}>
                          <LocationIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                          {space.external_name || space.name}
                        </div>
                        <div className={`text-xs mt-0.5 ml-6 flex items-center gap-2 ${
                          selectedId === space.id ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-600 dark:text-webex-gray-400"
                        }`}>
                          {space.name && space.external_name && <span>{space.name}</span>}
                          {space.capacity && <span>Capacity: {space.capacity}</span>}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {totalCount > 0 && (
              <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400">
                {totalCount > 20
                  ? `Showing 20 of ${totalCount} spaces. Refine your search to find more.`
                  : `${totalCount} space${totalCount === 1 ? "" : "s"} available`
                }
              </p>
            )}
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleAdd} disabled={!selectedId}>Link</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
